import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, X, Plus, Pencil, Trash2 } from "lucide-react";
import { api, fmtUSD, fmtDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const METHODS = ["Bank Wire", "Crypto (USDT)", "Skrill", "Neteller", "Cash", "Other"];
const STATUSES = ["pending", "approved", "rejected"];

function isoToLocalInput(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
function localInputToIso(v) {
  if (!v) return null;
  return new Date(v).toISOString();
}

function WdTable({ rows, onDecision, onEdit, onDelete }) {
  return (
    <div className="border border-subtle bg-card overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow className="bg-surface-hover border-subtle">
            <TableHead>Client</TableHead>
            <TableHead className="text-right">Amount</TableHead>
            <TableHead>Method</TableHead>
            <TableHead>Account</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Created</TableHead>
            <TableHead>Processed</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.length === 0 && (
            <TableRow><TableCell colSpan={8} className="text-center py-12 text-muted-foreground">No records</TableCell></TableRow>
          )}
          {rows.map((t) => (
            <TableRow key={t.id} className="border-subtle hover:bg-surface-hover" data-testid={`wd-row-${t.id}`}>
              <TableCell className="text-sm">{t.user_name}</TableCell>
              <TableCell className="text-right font-mono-num font-semibold">{fmtUSD(t.amount)}</TableCell>
              <TableCell>{t.method}</TableCell>
              <TableCell className="text-xs font-mono-num text-muted-foreground max-w-xs truncate">{t.account_details}</TableCell>
              <TableCell>
                <span className={`text-xs font-semibold px-2 py-0.5 rounded ${
                  t.status === "pending" ? "bg-warning/15 text-warning" :
                  t.status === "approved" ? "bg-profit/15 text-profit" :
                  "bg-loss/15 text-loss"
                }`}>{t.status.toUpperCase()}</span>
              </TableCell>
              <TableCell className="text-xs text-muted-foreground">{fmtDate(t.created_at)}</TableCell>
              <TableCell className="text-xs text-muted-foreground">{fmtDate(t.processed_at)}</TableCell>
              <TableCell className="text-right">
                <div className="flex gap-1 justify-end items-center">
                  {t.status === "pending" && (
                    <>
                      <button onClick={() => onDecision(t.id, "approve")} className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 bg-profit/10 text-profit hover:bg-profit hover:text-white transition-colors rounded font-medium" data-testid={`approve-wd-${t.id}`}>
                        <Check className="w-3 h-3" /> Approve
                      </button>
                      <button onClick={() => onDecision(t.id, "reject")} className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 bg-loss/10 text-loss hover:bg-loss hover:text-white transition-colors rounded font-medium" data-testid={`reject-wd-${t.id}`}>
                        <X className="w-3 h-3" /> Reject
                      </button>
                    </>
                  )}
                  <button onClick={() => onEdit(t)} className="p-1.5 hover:bg-surface-hover rounded" title="Edit" data-testid={`edit-wd-${t.id}`}>
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                  <button onClick={() => onDelete(t)} className="p-1.5 hover:bg-surface-hover rounded" title="Delete" data-testid={`delete-wd-${t.id}`}>
                    <Trash2 className="w-3.5 h-3.5 text-loss" />
                  </button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

export default function Withdrawals() {
  const [pending, setPending] = useState([]);
  const [history, setHistory] = useState([]);
  const [users, setUsers] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    const [a, b, u] = await Promise.all([
      api.get("/admin/transactions", { params: { type: "withdrawal", status: "pending" } }),
      api.get("/admin/transactions", { params: { type: "withdrawal" } }),
      api.get("/admin/users"),
    ]);
    setPending(a.data);
    setHistory(b.data.filter((t) => t.status !== "pending"));
    setUsers(u.data);
  };

  useEffect(() => { load(); }, []);

  const decide = async (id, action) => {
    try {
      await api.post(`/admin/transactions/${id}/decision`, { action });
      toast.success(`Withdrawal ${action}d`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete this withdrawal of ${fmtUSD(t.amount)} for ${t.user_name}? If it was approved, the client balance will be restored.`)) return;
    try {
      await api.delete(`/admin/transactions/${t.id}`);
      toast.success("Withdrawal deleted");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  return (
    <div data-testid="withdrawals-page">
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="label-eyebrow">Withdrawals management</p>
          <h3 className="text-lg font-semibold">Client withdrawals</h3>
        </div>
        <Button onClick={() => setAddOpen(true)} data-testid="add-withdrawal-btn" className="gap-2">
          <Plus className="w-4 h-4" /> Add Withdrawal
        </Button>
      </div>

      <Tabs defaultValue="pending">
        <TabsList className="bg-card border border-subtle">
          <TabsTrigger value="pending" data-testid="wd-tab-pending">Pending ({pending.length})</TabsTrigger>
          <TabsTrigger value="history" data-testid="wd-tab-history">History ({history.length})</TabsTrigger>
        </TabsList>
        <TabsContent value="pending" className="mt-4">
          <WdTable rows={pending} onDecision={decide} onEdit={setEditing} onDelete={remove} />
        </TabsContent>
        <TabsContent value="history" className="mt-4">
          <WdTable rows={history} onDecision={decide} onEdit={setEditing} onDelete={remove} />
        </TabsContent>
      </Tabs>

      <AddWithdrawalDialog open={addOpen} onOpenChange={setAddOpen} users={users} onCreated={load} />
      <EditWithdrawalDialog transaction={editing} onOpenChange={(o) => !o && setEditing(null)} onSaved={load} />
    </div>
  );
}

function AddWithdrawalDialog({ open, onOpenChange, users, onCreated }) {
  const [form, setForm] = useState({
    user_id: "",
    amount: "",
    method: METHODS[0],
    account_details: "",
    note: "",
    created_at: isoToLocalInput(new Date().toISOString()),
    auto_approve: true,
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({
        user_id: "",
        amount: "",
        method: METHODS[0],
        account_details: "",
        note: "",
        created_at: isoToLocalInput(new Date().toISOString()),
        auto_approve: true,
      });
    }
  }, [open]);

  const selectedUser = users.find((u) => u.id === form.user_id);

  const submit = async () => {
    if (!form.user_id) return toast.error("Select a client");
    const amount = parseFloat(form.amount);
    if (!amount || amount <= 0) return toast.error("Enter a valid amount");
    setBusy(true);
    try {
      await api.post("/admin/withdrawals", {
        user_id: form.user_id,
        amount,
        method: form.method,
        account_details: form.account_details,
        note: form.note,
        created_at: localInputToIso(form.created_at),
        auto_approve: form.auto_approve,
      });
      toast.success(`Withdrawal of ${fmtUSD(amount)} recorded`);
      onOpenChange(false);
      onCreated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add withdrawal");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-subtle max-w-lg max-h-[90vh] p-0 flex flex-col" data-testid="add-withdrawal-dialog">
        <DialogHeader className="px-6 pt-6 pb-2 shrink-0">
          <DialogTitle>Add Withdrawal</DialogTitle>
          <DialogDescription className="text-muted-foreground">
            Create a withdrawal for a client directly. Approved withdrawals debit the client&rsquo;s balance immediately.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 px-6 py-2 overflow-y-auto flex-1">
          <div>
            <Label className="label-eyebrow">Client</Label>
            <Select value={form.user_id} onValueChange={(v) => setForm({ ...form, user_id: v })}>
              <SelectTrigger className="mt-2 bg-background border-subtle h-10" data-testid="add-wd-client">
                <SelectValue placeholder="Select a client…" />
              </SelectTrigger>
              <SelectContent>
                {users.map((u) => (
                  <SelectItem key={u.id} value={u.id}>{u.full_name} — {u.email} ({fmtUSD(u.balance)})</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selectedUser && (
              <p className="text-xs text-muted-foreground mt-1.5 font-mono-num">
                Available: {fmtUSD(selectedUser.balance)}
              </p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="label-eyebrow">Amount (USD)</Label>
              <Input type="number" step="0.01" min="0" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="mt-2 bg-background border-subtle font-mono-num h-10" data-testid="add-wd-amount" />
            </div>
            <div>
              <Label className="label-eyebrow">Method</Label>
              <Select value={form.method} onValueChange={(v) => setForm({ ...form, method: v })}>
                <SelectTrigger className="mt-2 bg-background border-subtle h-10" data-testid="add-wd-method"><SelectValue /></SelectTrigger>
                <SelectContent>{METHODS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label className="label-eyebrow">Date &amp; Time</Label>
            <Input type="datetime-local" value={form.created_at} onChange={(e) => setForm({ ...form, created_at: e.target.value })} className="mt-2 bg-background border-subtle font-mono-num h-10" data-testid="add-wd-date" />
          </div>

          <div>
            <Label className="label-eyebrow">Account / Wallet Details</Label>
            <Textarea value={form.account_details} onChange={(e) => setForm({ ...form, account_details: e.target.value })} className="mt-2 bg-background border-subtle font-mono-num text-sm" rows={2} placeholder="IBAN / wallet address / account #" data-testid="add-wd-account" />
          </div>

          <div>
            <Label className="label-eyebrow">Note</Label>
            <Textarea value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className="mt-2 bg-background border-subtle" rows={2} placeholder="Optional" data-testid="add-wd-note" />
          </div>

          <label className="flex items-center gap-2 cursor-pointer select-none">
            <input type="checkbox" checked={form.auto_approve} onChange={(e) => setForm({ ...form, auto_approve: e.target.checked })} data-testid="add-wd-auto-approve" className="w-4 h-4 rounded border-subtle accent-primary" />
            <span className="text-sm">Auto-approve &amp; debit client balance immediately</span>
          </label>
        </div>

        <DialogFooter className="px-6 pb-6 pt-3 border-t border-subtle shrink-0">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="add-wd-cancel">Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="add-wd-save">
            {busy ? "Adding…" : "Add Withdrawal"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditWithdrawalDialog({ transaction, onOpenChange, onSaved }) {
  const open = !!transaction;
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (transaction) {
      setForm({
        amount: transaction.amount,
        method: transaction.method,
        account_details: transaction.account_details || "",
        note: transaction.note || "",
        status: transaction.status,
        created_at: isoToLocalInput(transaction.created_at),
        processed_at: isoToLocalInput(transaction.processed_at),
        admin_note: transaction.admin_note || "",
      });
    }
  }, [transaction]);

  if (!open || !form) return null;

  const submit = async () => {
    const amount = parseFloat(form.amount);
    if (!amount || amount <= 0) return toast.error("Enter a valid amount");
    setBusy(true);
    try {
      const payload = {
        amount,
        method: form.method,
        account_details: form.account_details,
        note: form.note,
        status: form.status,
        created_at: localInputToIso(form.created_at),
        processed_at: form.processed_at ? localInputToIso(form.processed_at) : null,
        admin_note: form.admin_note,
      };
      await api.patch(`/admin/transactions/${transaction.id}`, payload);
      toast.success("Withdrawal updated");
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-subtle max-w-lg max-h-[90vh] p-0 flex flex-col" data-testid="edit-withdrawal-dialog">
        <DialogHeader className="px-6 pt-6 pb-2 shrink-0">
          <DialogTitle>Edit Withdrawal</DialogTitle>
          <DialogDescription className="text-muted-foreground">
            Changing amount or status on an approved withdrawal will automatically reconcile the client&rsquo;s balance.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 px-6 py-2 overflow-y-auto flex-1">
          <div className="text-xs text-muted-foreground">
            Client: <span className="text-foreground font-medium">{transaction.user_name}</span>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="label-eyebrow">Amount (USD)</Label>
              <Input type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="mt-2 bg-background border-subtle font-mono-num h-10" data-testid="edit-wd-amount" />
            </div>
            <div>
              <Label className="label-eyebrow">Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger className="mt-2 bg-background border-subtle h-10" data-testid="edit-wd-status"><SelectValue /></SelectTrigger>
                <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s.toUpperCase()}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label className="label-eyebrow">Method</Label>
            <Select value={form.method} onValueChange={(v) => setForm({ ...form, method: v })}>
              <SelectTrigger className="mt-2 bg-background border-subtle h-10" data-testid="edit-wd-method"><SelectValue /></SelectTrigger>
              <SelectContent>{METHODS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="label-eyebrow">Created</Label>
              <Input type="datetime-local" value={form.created_at} onChange={(e) => setForm({ ...form, created_at: e.target.value })} className="mt-2 bg-background border-subtle font-mono-num h-10" data-testid="edit-wd-created" />
            </div>
            <div>
              <Label className="label-eyebrow">Processed</Label>
              <Input type="datetime-local" value={form.processed_at} onChange={(e) => setForm({ ...form, processed_at: e.target.value })} className="mt-2 bg-background border-subtle font-mono-num h-10" data-testid="edit-wd-processed" />
            </div>
          </div>

          <div>
            <Label className="label-eyebrow">Account / Wallet Details</Label>
            <Textarea value={form.account_details} onChange={(e) => setForm({ ...form, account_details: e.target.value })} className="mt-2 bg-background border-subtle font-mono-num text-sm" rows={2} data-testid="edit-wd-account" />
          </div>

          <div>
            <Label className="label-eyebrow">Note</Label>
            <Textarea value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className="mt-2 bg-background border-subtle" rows={2} data-testid="edit-wd-note" />
          </div>

          <div>
            <Label className="label-eyebrow">Admin Note</Label>
            <Textarea value={form.admin_note} onChange={(e) => setForm({ ...form, admin_note: e.target.value })} className="mt-2 bg-background border-subtle" rows={2} data-testid="edit-wd-admin-note" />
          </div>
        </div>

        <DialogFooter className="px-6 pb-6 pt-3 border-t border-subtle shrink-0">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="edit-wd-cancel">Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="edit-wd-save">
            {busy ? "Saving…" : "Save Changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
